import { Fragment } from "react";

import { getMetricLabels, type RegionId } from "@/lib/tci";
import { cn } from "@/lib/utils";

export type CalculatorStepId = 1 | 2 | 3;

export type ClientStepperProps = {
  currentStep: CalculatorStepId;
  regionId?: RegionId | null;
  className?: string;
};

/**
 * Three-step TCI calculator progress (Figma 377:1717).
 * Labels sourced from engine metric labels (region spelling applied).
 */
export function ClientStepper({
  currentStep,
  regionId,
  className,
}: ClientStepperProps) {
  const metricLabels = getMetricLabels(regionId);
  const steps = [
    { id: 1 as const, label: metricLabels.labor.short },
    { id: 2 as const, label: metricLabels.yieldPenalty.short },
    { id: 3 as const, label: metricLabels.untapped.short },
  ];

  return (
    <nav
      aria-label="Calculator steps"
      className={cn("flex w-full items-center gap-2", className)}
    >
      {steps.map((step, index) => {
        const isActive = step.id === currentStep;
        const isLast = index === steps.length - 1;

        return (
          <Fragment key={step.id}>
            <div className="flex shrink-0 items-center gap-2">
              <span
                aria-current={isActive ? "step" : undefined}
                className={cn(
                  "inline-flex size-9 shrink-0 items-center justify-center rounded-[18px] border p-2 font-['Inter'] text-sm font-medium leading-5 tracking-[-0.35px] backdrop-blur-[4px]",
                  isActive
                    ? "border-[#38bdf8] bg-[#38bdf8] text-[#0c1b2e]"
                    : "border-[rgba(56,189,248,0.35)] bg-[rgba(11,22,40,0.42)] text-[#9194a1]",
                )}
              >
                {step.id}
              </span>
              <span
                className={cn(
                  "hidden whitespace-nowrap font-['Inter'] text-[20px] font-normal leading-7 tracking-[-1.2px] sm:inline",
                  isActive ? "text-[#f5f3ee]" : "text-[#9194a1]",
                )}
              >
                {step.label}
              </span>
              <span
                className={cn(
                  "whitespace-nowrap font-['Inter'] text-xs font-normal leading-5 tracking-[-0.4px] sm:hidden",
                  isActive ? "text-[#f5f3ee]" : "text-[#9194a1]",
                )}
              >
                {step.label.split(" ")[0]}
              </span>
            </div>

            {!isLast ? (
              <div
                aria-hidden
                className="h-px min-w-[8px] flex-1 bg-[rgba(145,148,161,0.45)]"
              />
            ) : null}
          </Fragment>
        );
      })}
    </nav>
  );
}
